import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "styled-components";

import { OutroPropsExtented } from "@/common/Outro";
import PostRegistrationPromo from "@/common/Outro/RegistrationForm/promo/PostRegistrationPromo";
import LightRegistrationForm from "@/common/Outro/RegistrationForm/templates/light/LightRegistrationForm";
import {
  FormButton,
  FormCheckbox,
  FormGroup,
  FormInput,
  FormStatic,
} from "@/components/Form";
import FormSelect from "@/components/Form/Select";
import { getApiUrl, getDocumentUrl, PRIVACY_STATEMENT_LINK_AS } from "@/config";
import { GlobalContext } from "@/contexts";
import { useTimeout } from "@/hooks";
import LocaleContext from "@/i18n";
import EventSystem from "@/service/EventSystem";
import IFrameService, { ParticipationTokenResponse } from "@/service/IFrame";
import InteractionService from "@/service/Interaction";
import {
  AutofillContact,
  CustomField,
  CustomFieldType,
  NewContactInput,
  Participant,
  ParticipantAutofill,
  ParticipationSubmit,
  RegistrationFormInteractionOutro,
  RegistrationStatus,
} from "@/service/Model";
import Svg from "@/styles/utils/Svg";
import { log } from "@/utils/logger";

import { OutroFeedback, OutroFeedbackIcon } from "../styles";
import ClassicRegistrationForm from "./templates/classic/ClassicRegistrationForm";
import { RegistrationStep } from "./templates/classic/styles";

const TIMEOUT_TO_RECEIVE_POST_MESSAGE_WITH_HANS_ID = 200;
const TIMEOUT_TO_INITIALIZE_SMART_FLOW = 3000;
const INITIAL_VISIBLE_CONTACTS = 4;

export interface RegistrationFormProps extends OutroPropsExtented {
  registrationStatus?: RegistrationStatus;
  hansId?: string;
  hansIdExists?: boolean;
  onSubmit?: (payload: Record<string, string | boolean> | Participant) => void;
  onSubmitEnd?: (status: RegistrationStatus) => void;
  outro: RegistrationFormInteractionOutro;
  lightLayout: boolean;
  initialParticipant?: Partial<Participant>;
  formTitle?: string;
  submitLabel?: string;
  successMessage?: string;
  submitNotice?: React.ReactNode;
  saveContactConsentControl?: React.ReactNode;
}

type AutofillState = {
  token: string;
  profile: ParticipantAutofill;
};

type SmartFlowState =
  | "loading"
  | "loginRequired"
  | "alreadyRegistered"
  | "chooseAddress"
  | "identityMismatch"
  | "manual"
  | "edit";

const SUBJECT_LABEL = "Anmerkungen";
// Key that earlier versions of this widget used to park pre-login form state (restore removed in
// ESD-2418). Nothing writes it anymore; a mount-time sweep purges any value a returning user's tab
// might still carry, so stale PII is actively cleared rather than left dormant.
const LEGACY_PENDING_KEY_PREFIX = "iat.autofill.pending.";

const normalizedName = (value?: string): string =>
  (value ?? "").trim().toLocaleLowerCase();

const enteredNameDiffersFromIdentity = (
  participant: Partial<Participant> | undefined,
  profile: ParticipantAutofill
): boolean => {
  const enteredFirstName = normalizedName(participant?.firstName);
  const enteredLastName = normalizedName(participant?.lastName);
  if (!enteredFirstName && !enteredLastName) {
    return false;
  }

  const savedFirstName = normalizedName(profile.identity.firstName);
  const savedLastName = normalizedName(profile.identity.lastName);
  return (
    (!!enteredFirstName && enteredFirstName !== savedFirstName) ||
    (!!enteredLastName && enteredLastName !== savedLastName)
  );
};

const resolveSmartFlowState = (
  profile: ParticipantAutofill,
  initialParticipant: Partial<Participant> | undefined
): SmartFlowState => {
  if (profile.alreadyRegistered === true) {
    return "alreadyRegistered";
  }
  if (profile.contacts.length === 0) {
    return "manual";
  }
  if (enteredNameDiffersFromIdentity(initialParticipant, profile)) {
    return "identityMismatch";
  }
  return "chooseAddress";
};

export const RegistrationForm: React.FC<RegistrationFormProps> = (
  props: RegistrationFormProps
): React.JSX.Element => {
  const {
    onSubmitEnd,
    lightLayout,
    outro: { hansIdOptIn },
  } = props;
  // Autofill only applies to full-address forms. Light variants (lightLayout / lightRegistration)
  // collect name+email only, so a NewContact can never be built — the consent, v2 submit and login
  // redirect must not engage there; those forms fall through to the legacy path unchanged.
  const autofillApplies =
    props.outro.autofillEnabled === true &&
    !lightLayout &&
    props.outro.lightRegistration !== true;
  const globalContext = useContext(GlobalContext);
  const interactionID = globalContext.response?.interaction.id;
  const [hansIdExists, setHansIdExists] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [autofill, setAutofill] = useState<AutofillState>();
  const [smartFlowState, setSmartFlowState] =
    useState<SmartFlowState>("loading");
  const [selectedContactId, setSelectedContactId] = useState<
    string | undefined
  >();
  const [showAllContacts, setShowAllContacts] = useState<boolean>(false);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string>();
  const [deletingContactId, setDeletingContactId] = useState<string>();
  const [deleteContactErrorId, setDeleteContactErrorId] = useState<string>();
  // Consent controls only whether a newly entered address is reusable by future Gewinnspiele. Every
  // authenticated entry uses v2 so the csuID-bound one-entry guard is independent of this choice.
  const [saveContactConsent, setSaveContactConsent] = useState<boolean>(false);
  const [autofillAdvertisementOptIn, setAutofillAdvertisementOptIn] =
    useState<boolean>(false);
  const [autofillSubject, setAutofillSubject] = useState<string>("");
  const [autofillCustomFields, setAutofillCustomFields] = useState<string[]>(
    []
  );
  const [autofillRegistrationStatus, setAutofillRegistrationStatus] =
    useState<RegistrationStatus>();
  const [contactUpdateStatus, setContactUpdateStatus] =
    useState<RegistrationStatus>();
  const localeContext = useContext(LocaleContext);
  const { translation } = localeContext;
  const theme = useTheme();
  const { registrationStatus, preview, hansId, handleRegistration, icons } =
    globalContext;
  // hansId reference is needed for timeout callback
  const hansIdRef = useRef<string | undefined>(hansId);

  const api = useMemo(
    () => new InteractionService(getApiUrl(preview)),
    [preview]
  );

  useEffect(() => {
    // Purge EVERY pre-login form-state key parked by earlier versions of this widget (ESD-2418),
    // across all interactions — not just the current one: a returning user is driven by their
    // account, and stale PII under any stale interaction key must be cleared, not just ignored.
    try {
      const storage = window.sessionStorage;
      const staleKeys: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(LEGACY_PENDING_KEY_PREFIX)) {
          staleKeys.push(key);
        }
      }
      // Collect first, then remove: mutating sessionStorage while iterating by index shifts entries.
      staleKeys.forEach((key) => storage.removeItem(key));
    } catch {
      // sessionStorage may be unavailable; the form is unaffected.
    }
  }, []);

  useEffect(() => {
    // Get hansId from adLib - useEffect on 'hansId' will be triggered
    IFrameService.getHansId();
    if (!autofillApplies) {
      return;
    }

    let active = true;
    let smartFlowPending = true;
    let smartFlowTimeout = 0;
    const finishSmartFlow = (state: SmartFlowState): boolean => {
      if (!active || !smartFlowPending) {
        return false;
      }
      smartFlowPending = false;
      window.clearTimeout(smartFlowTimeout);
      setSmartFlowState(state);
      return true;
    };
    const fallbackToManual = (): void => {
      finishSmartFlow("manual");
    };
    const unsubscribe = EventSystem.subscribe(
      "participationToken:received",
      (data: ParticipationTokenResponse | undefined) => {
        if (!active || !smartFlowPending) {
          return;
        }
        if (data?.status === "unauthenticated") {
          finishSmartFlow("loginRequired");
          return;
        }
        if (data?.status === "unavailable") {
          fallbackToManual();
          return;
        }
        if (data?.status !== "authenticated" || !data.participationToken) {
          fallbackToManual();
          return;
        }
        if (!interactionID) {
          fallbackToManual();
          return;
        }
        const participationToken = data.participationToken;
        api
          .getParticipantAutofill(participationToken, interactionID)
          .then((response) => {
            if (!active || !response.ok || !response.data) {
              if (active) {
                fallbackToManual();
              }
              return;
            }
            // getParticipantAutofill normalises contacts to [] for every caller, so response.data
            // is safe to use directly here and on the refresh paths below.
            const profile = response.data;
            // Only a complete participation is true. The API maps pending/contact_saved to false so
            // recoverable submissions stay in this normal submit state machine instead of the edit-only UI.
            const nextState = resolveSmartFlowState(
              profile,
              props.initialParticipant
            );
            if (!finishSmartFlow(nextState)) {
              return;
            }
            setAutofill({
              token: participationToken,
              profile,
            });
            setSelectedContactId(undefined);
          })
          .catch(() => {
            if (active) {
              fallbackToManual();
            }
          });
      }
    );
    smartFlowTimeout = window.setTimeout(
      fallbackToManual,
      TIMEOUT_TO_INITIALIZE_SMART_FLOW
    );
    IFrameService.getParticipationToken();
    return () => {
      active = false;
      smartFlowPending = false;
      window.clearTimeout(smartFlowTimeout);
      unsubscribe();
    };
  }, [
    api,
    interactionID,
    autofillApplies,
    props.initialParticipant?.firstName,
    props.initialParticipant?.lastName,
  ]);

  useEffect(() => {
    hansIdRef.current = hansId;
    if (!hansId || !hansIdOptIn) {
      // hansId is not received or checkbox setting is not configured - no query from the database necessary
      setLoading(false);
      return;
    }
    verifyConsentForHansId(hansId);
  }, [hansId]);

  useTimeout(() => {
    // - Checkbox setting is configured
    // - No hansId received
    // - Not preview mode
    if (hansIdOptIn && !hansIdRef.current && !preview) {
      // Possible reasons:
      // - The use of the Hans-ID can be rejected by the user via a cookie banner, etc.
      // - Timing problem: PostMessage sender and recipient miss each other
      log.info(
        "Registration form without hans-opt-in. Reason: No hans ID received. Referrer: " +
          getDocumentUrl()
      );
    }
  }, TIMEOUT_TO_RECEIVE_POST_MESSAGE_WITH_HANS_ID);

  const verifyConsentForHansId = (hansId: string) => {
    api
      .hansIdExists(hansId)
      .then((exists: boolean) => {
        setHansIdExists(exists);
      })
      .catch(() => {
        setHansIdExists(false);
      })
      .then(() => {
        setLoading(false);
      });
  };

  const handleSubmit = (
    payload: Record<string, string | boolean> | Participant
  ) => {
    if (autofillApplies && autofill) {
      const autofillPayload = buildAutofillPayload(payload);
      if (!autofillPayload) {
        setAutofillRegistrationStatus(RegistrationStatus.Error);
        onSubmitEnd?.(RegistrationStatus.Error);
        return Promise.resolve(RegistrationStatus.Error);
      }
      return submitAutofillParticipation(autofillPayload).then(
        (status: RegistrationStatus) => onSubmitEnd?.(status)
      );
    }

    return handleRegistration(payload).then((status: RegistrationStatus) =>
      onSubmitEnd?.(status)
    );
  };

  const buildBaseAutofillPayload = (
    payload: Partial<ParticipationSubmit>
  ): ParticipationSubmit | undefined => {
    const interaction = globalContext.response?.interaction;
    if (!autofill || !interaction) {
      return undefined;
    }
    return {
      interactionID: interaction.id,
      interactionType: interaction.type,
      campaignId: interaction.outro?.campaignId,
      ...payload,
    };
  };

  const buildAutofillPayload = (
    payload: Record<string, string | boolean> | Participant
  ): ParticipationSubmit | undefined => {
    if (!autofill) {
      return undefined;
    }

    const data = payload as any;
    const newContact = buildNewContact(data);
    if (!newContact) {
      return undefined;
    }

    return buildBaseAutofillPayload({
      advertisementOptIn: data.advertisementOptIn === true,
      subject: typeof data.subject === "string" ? data.subject : undefined,
      customFields: Array.isArray(data.customFields)
        ? data.customFields
        : undefined,
      newContact,
      saveContactConsent,
    });
  };

  const buildNewContact = (payload: any): NewContactInput | undefined => {
    const firstName = String(payload.firstName ?? "");
    const lastName = String(payload.lastName ?? "");
    const email = String(payload.email ?? "");
    const street = String(payload.street ?? "");
    const houseNumber = String(payload.houseNumber ?? "");
    const postalCode = String(payload.postalcode ?? payload.postalCode ?? "");
    const city = String(payload.city ?? "");

    if (
      !firstName ||
      !lastName ||
      !email ||
      !street ||
      !houseNumber ||
      !postalCode ||
      !city
    ) {
      return undefined;
    }

    return {
      salutation: String(payload.gender ?? ""),
      gender: String(payload.gender ?? ""),
      // The form's salutation is always the source of truth here (it defaults to "Keine Angabe"
      // and the user can change it), so mark it authoritative: the backend must persist it as sent
      // — blank included — and never re-derive salutation/gender from the account (ESD-2418 §5 O2).
      salutationProvided: true,
      firstName,
      lastName,
      email,
      telephone: String(payload.telephone ?? ""),
      street,
      houseNumber,
      postalCode,
      city,
    };
  };

  const submitAutofillParticipation = async (
    payload: ParticipationSubmit
  ): Promise<RegistrationStatus> => {
    if (!autofill) {
      return RegistrationStatus.Error;
    }

    setAutofillRegistrationStatus(RegistrationStatus.Pending);
    try {
      const response = await api.postParticipationV2(payload, autofill.token);
      let alreadyRegistered = response.data?.alreadyRegistered === true;
      if (response.status === 409 && !alreadyRegistered) {
        try {
          alreadyRegistered =
            JSON.parse(response.text)?.alreadyRegistered === true;
        } catch {
          // A malformed or unknown conflict is handled by the generic error path below.
        }
      }
      if (alreadyRegistered && (response.ok || response.status === 409)) {
        const currentAutofill = autofill;
        let refreshedProfile: ParticipantAutofill | undefined;
        if (interactionID) {
          let refreshTimeout = 0;
          try {
            const refreshed = await Promise.race([
              api.getParticipantAutofill(autofill.token, interactionID),
              new Promise<undefined>((resolve) => {
                refreshTimeout = window.setTimeout(
                  () => resolve(undefined),
                  TIMEOUT_TO_INITIALIZE_SMART_FLOW
                );
              }),
            ]);
            if (refreshed?.ok) {
              refreshedProfile = refreshed.data;
            }
          } catch {
            // The save response is authoritative; keep the stale profile only as a UI fallback.
          } finally {
            window.clearTimeout(refreshTimeout);
          }
        }
        setAutofill({
          token: currentAutofill.token,
          profile: {
            ...(refreshedProfile ?? currentAutofill.profile),
            alreadyRegistered: true,
          },
        });
        setSelectedContactId(undefined);
        setAutofillRegistrationStatus(undefined);
        setSmartFlowState("alreadyRegistered");
        return RegistrationStatus.None;
      }
      if (response.ok || response.status === 202) {
        setAutofillRegistrationStatus(RegistrationStatus.Success);
        return RegistrationStatus.Success;
      }
      setAutofillRegistrationStatus(RegistrationStatus.Error);
      log.error(
        `Error while submit autofill registration form: ${response.status}: ${response.text}`
      );
      return RegistrationStatus.Error;
    } catch (e) {
      setAutofillRegistrationStatus(RegistrationStatus.Error);
      log.error("Error while submit autofill registration form:" + e);
      return RegistrationStatus.Error;
    }
  };

  const handleContactUpdate = async (
    payload: Record<string, string | boolean> | Participant
  ): Promise<void> => {
    const contact = buildNewContact(payload as any);
    if (!autofill || !interactionID || !contact) {
      setContactUpdateStatus(RegistrationStatus.Error);
      return;
    }

    setContactUpdateStatus(RegistrationStatus.Pending);
    try {
      const response = await api.putParticipationContact(
        interactionID,
        contact,
        autofill.token
      );
      if (!response.ok) {
        if (response.status === 404 || response.status === 409) {
          const refreshed = await api.getParticipantAutofill(
            autofill.token,
            interactionID
          );
          if (!refreshed.ok || !refreshed.data) {
            setContactUpdateStatus(RegistrationStatus.Error);
            return;
          }
          setAutofill({ token: autofill.token, profile: refreshed.data });
          setSelectedContactId(undefined);
          setContactUpdateStatus(undefined);
          setSmartFlowState(
            resolveSmartFlowState(refreshed.data, props.initialParticipant)
          );
          return;
        }
        setContactUpdateStatus(RegistrationStatus.Error);
        return;
      }
      setAutofill((current) =>
        current
          ? {
              ...current,
              profile: {
                ...current.profile,
                contacts: current.profile.contacts.map((saved, index) =>
                  index === 0 ? { ...saved, ...contact } : saved
                ),
                entryContact: current.profile.entryContact
                  ? { ...current.profile.entryContact, ...contact }
                  : current.profile.entryContact,
                entrySummary: {
                  firstName: contact.firstName,
                  city: contact.city,
                },
              },
            }
          : current
      );
      setContactUpdateStatus(RegistrationStatus.Success);
    } catch (error) {
      setContactUpdateStatus(RegistrationStatus.Error);
      log.error("Error while updating autofill contact:" + error);
    }
  };

  const handleContactDelete = async (contactId: string): Promise<void> => {
    if (!autofill || !interactionID || deletingContactId) {
      return;
    }

    setDeletingContactId(contactId);
    setDeleteContactErrorId(undefined);
    let deleted = false;
    try {
      const response = await api.deleteParticipantAutofillContact(
        contactId,
        autofill.token
      );
      deleted = response.ok;
    } catch (error) {
      log.error("Error while deleting autofill contact:" + error);
    }
    if (!deleted) {
      setDeleteContactErrorId(contactId);
      setDeleteConfirmationId(undefined);
      setDeletingContactId(undefined);
      return;
    }

    const profileWithoutDeleted = {
      ...autofill.profile,
      contacts: autofill.profile.contacts.filter(
        (contact) => contact.id !== contactId
      ),
    };
    setAutofill({ token: autofill.token, profile: profileWithoutDeleted });
    setSelectedContactId((current) =>
      current === contactId ? undefined : current
    );
    setDeleteConfirmationId(undefined);
    setSmartFlowState(
      resolveSmartFlowState(profileWithoutDeleted, props.initialParticipant)
    );

    try {
      const refreshed = await api.getParticipantAutofill(
        autofill.token,
        interactionID
      );
      if (refreshed.ok && refreshed.data) {
        setAutofill({ token: autofill.token, profile: refreshed.data });
        setSmartFlowState(
          resolveSmartFlowState(refreshed.data, props.initialParticipant)
        );
      }
    } catch (error) {
      log.error(
        "Error while refreshing autofill contacts after delete:" + error
      );
    }
    setDeletingContactId(undefined);
  };

  const handleSelectedContactSubmit = (): void => {
    if (!selectedContactId || !validateAutofillChooser()) {
      return;
    }

    const payload = buildBaseAutofillPayload({
      advertisementOptIn: autofillAdvertisementOptIn,
      subject: props.outro.registrationCustomOption
        ? autofillSubject
        : undefined,
      customFields: props.outro.customFields?.length
        ? props.outro.customFields.map(
            (_customField: CustomField, index: number) =>
              autofillCustomFields[index] ?? ""
          )
        : undefined,
      selectedContactId,
    });
    if (!payload) {
      return;
    }

    submitAutofillParticipation(payload).then((status: RegistrationStatus) =>
      onSubmitEnd?.(status)
    );
  };

  const handleAutofillCustomFieldChange = (
    index: number,
    value: string
  ): void => {
    setAutofillCustomFields((currentCustomFields) => {
      const nextCustomFields = [...currentCustomFields];
      nextCustomFields[index] = value;
      return nextCustomFields;
    });
  };

  const validateAutofillChooser = (): boolean => {
    if (props.outro.registrationCustomOption && autofillSubject.length === 0) {
      return false;
    }

    return (props.outro.customFields ?? []).every(
      (_customField: CustomField, index: number) =>
        (autofillCustomFields[index] ?? "").length > 0
    );
  };

  const formatContact = (contact: AutofillContact): string =>
    [
      [contact.firstName, contact.lastName].filter(Boolean).join(" "),
      [contact.street, contact.houseNumber].filter(Boolean).join(" "),
      [contact.postalCode, contact.city].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ");

  const getInitialParticipant = (): Partial<Participant> | undefined => {
    if (!autofill?.profile.identity) {
      return props.initialParticipant;
    }

    return {
      firstName: autofill.profile.identity.firstName,
      lastName: autofill.profile.identity.lastName,
      email: autofill.profile.identity.email,
      gender: autofill.profile.identity.gender,
      telephone: autofill.profile.identity.telephone,
      ...props.initialParticipant,
    };
  };

  const getEntryContactParticipant = (): Partial<Participant> | undefined => {
    const contact =
      autofill?.profile.entryContact ??
      (autofill?.profile.entryContactState === "available"
        ? autofill.profile.contacts[0]
        : undefined);
    if (!contact) {
      return getInitialParticipant();
    }
    return {
      firstName: contact.firstName ?? "",
      lastName: contact.lastName ?? "",
      email: contact.email ?? autofill?.profile.identity.email ?? "",
      gender: contact.gender ?? contact.salutation ?? "",
      telephone: contact.telephone ?? "",
      street: contact.street ?? "",
      houseNumber: contact.houseNumber ?? "",
      postalcode: contact.postalCode ?? "",
      city: contact.city ?? "",
    };
  };

  const renderAutofillStatus = (
    status: RegistrationStatus
  ): React.JSX.Element => {
    let icon = null;
    let feedback = translation.request.pending;

    if (status === RegistrationStatus.Error) {
      icon = icons.Fail;
      feedback = translation.request.error;
    } else if (status === RegistrationStatus.Success) {
      icon = icons.Success;
      feedback = translation.thankYou;
    }

    return (
      <>
        <OutroFeedback
          $registration={true}
          data-qa="registration.outro.feedback"
        >
          {icon && (
            <OutroFeedbackIcon
              $registration={true}
              $success={status === RegistrationStatus.Success}
            >
              {/* ESD-2418 §3.10: lighter BILD-CI confirmation — matches the classic/light tick. */}
              <Svg contents={icon} height={44} width={44} />
            </OutroFeedbackIcon>
          )}
          <h3>{feedback}</h3>
        </OutroFeedback>
        {status === RegistrationStatus.Success &&
          props.outro.promo?.enabled && (
            <PostRegistrationPromo outro={props.outro} />
          )}
      </>
    );
  };

  const renderAutofillChooser = (): React.JSX.Element => {
    const currentStatus = autofillRegistrationStatus ?? registrationStatus;
    if (currentStatus && currentStatus !== RegistrationStatus.None) {
      return renderAutofillStatus(currentStatus);
    }

    const contactOptions =
      autofill?.profile.contacts.map((contact) => ({
        label: formatContact(contact),
        value: contact.id,
      })) ?? [];
    const hasMoreContacts = contactOptions.length > INITIAL_VISIBLE_CONTACTS;
    const visibleContactOptions = showAllContacts
      ? contactOptions
      : contactOptions.slice(0, INITIAL_VISIBLE_CONTACTS);
    const submitDisabled = !selectedContactId || !validateAutofillChooser();

    return (
      <RegistrationStep data-qa="registration.autofill.chooser">
        <FormStatic>
          <b>
            {smartFlowState === "identityMismatch"
              ? translation.registration.autofillIdentityMismatch
              : translation.registration.autofillChooserTitle}
          </b>
        </FormStatic>
        <FormStatic>
          <div
            data-qa="registration.autofill.existingAddressNotice"
            style={autofillStyles.legalNotice}
          >
            {translation.registration.autofillExistingAddressNotice}
          </div>
        </FormStatic>
        <div
          aria-label={translation.registration.autofillChooserTitle}
          data-qa="registration.autofill.contacts"
          role="radiogroup"
          style={{
            ...autofillStyles.options,
            ...(showAllContacts && hasMoreContacts
              ? autofillStyles.scrollOptions
              : undefined),
          }}
        >
          {visibleContactOptions.map((option) => {
            const deleteConfirmationLabel = `${translation.registration.autofillDeleteConfirmation} ${option.label}`;

            return (
              <div key={option.value} style={autofillStyles.contactRow}>
                {deleteConfirmationId === option.value ? (
                  <div
                    aria-label={deleteConfirmationLabel}
                    role="alertdialog"
                    style={autofillStyles.deleteConfirmation}
                  >
                    <span style={autofillStyles.deleteConfirmationText}>
                      {deleteConfirmationLabel}
                    </span>
                    <div style={autofillStyles.deleteConfirmationActions}>
                      <button
                        autoFocus
                        type="button"
                        data-qa={`registration.autofill.delete.cancel.${option.value}`}
                        disabled={deletingContactId !== undefined}
                        onClick={() => {
                          setDeleteConfirmationId(undefined);
                          setDeleteContactErrorId(undefined);
                        }}
                        style={{
                          ...autofillStyles.contactAction,
                          borderColor: theme.colors.borderGrey,
                          color: theme.colors.fontText,
                        }}
                      >
                        {translation.registration.autofillCancelDelete}
                      </button>
                      <button
                        type="button"
                        data-qa={`registration.autofill.delete.confirm.${option.value}`}
                        disabled={deletingContactId !== undefined}
                        onClick={() => handleContactDelete(option.value)}
                        style={{
                          ...autofillStyles.contactAction,
                          backgroundColor: theme.colors.error,
                          borderColor: theme.colors.error,
                          color: theme.colors.buttonText,
                        }}
                      >
                        {translation.registration.autofillConfirmDelete}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <label style={autofillStyles.option}>
                      <input
                        data-qa={`registration.autofill.contact.${option.value}`}
                        type="radio"
                        name="autofillContact"
                        value={option.value}
                        checked={selectedContactId === option.value}
                        onChange={() => setSelectedContactId(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                    <button
                      type="button"
                      data-qa={`registration.autofill.delete.${option.value}`}
                      aria-label={`${translation.registration.autofillDeleteAddress}: ${option.label}`}
                      disabled={deletingContactId !== undefined}
                      onClick={() => {
                        setDeleteContactErrorId(undefined);
                        setDeleteConfirmationId(option.value);
                      }}
                      style={{
                        ...autofillStyles.contactAction,
                        borderColor: theme.colors.button,
                        color: theme.colors.button,
                      }}
                    >
                      {translation.registration.autofillDeleteAddress}
                    </button>
                  </>
                )}
                {deleteContactErrorId === option.value && (
                  <span
                    role="alert"
                    style={{ color: theme.colors.error, width: "100%" }}
                  >
                    {translation.request.error}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {hasMoreContacts && !showAllContacts && (
          <FormButton
            type="button"
            label={translation.registration.autofillShowMoreAddresses(
              contactOptions.length - INITIAL_VISIBLE_CONTACTS
            )}
            onClick={() => setShowAllContacts(true)}
            dataQA="registration.autofill.showMore"
            variant="secondary"
          />
        )}
        {props.outro.registrationCustomOption && (
          <FormGroup>
            <FormInput
              name="autofillSubject"
              label={SUBJECT_LABEL}
              required={{
                active: true,
                text: translation.registration.required,
              }}
              value={autofillSubject}
              onChange={setAutofillSubject}
              valid={true}
            />
          </FormGroup>
        )}
        {(props.outro.customFields ?? []).map(
          (customField: CustomField, index: number) => {
            const name = `autofillCustomField-${index}`;
            const value = autofillCustomFields[index] ?? "";
            return (
              <FormGroup key={name}>
                {customField.type === CustomFieldType.Select ? (
                  <FormSelect
                    name={name}
                    label={customField.label}
                    required={{
                      active: true,
                      text: translation.registration.required,
                    }}
                    value={value}
                    options={customField.options ?? []}
                    onChange={(nextValue: string) =>
                      handleAutofillCustomFieldChange(index, nextValue)
                    }
                    valid={true}
                  />
                ) : (
                  <FormInput
                    name={name}
                    label={customField.label}
                    required={{
                      active: true,
                      text: translation.registration.required,
                    }}
                    value={value}
                    onChange={(nextValue: string) =>
                      handleAutofillCustomFieldChange(index, nextValue)
                    }
                    valid={true}
                  />
                )}
              </FormGroup>
            );
          }
        )}
        {props.outro.advertisementOptIn && (
          <div style={autofillStyles.checkbox}>
            <FormCheckbox
              icons={icons}
              name="autofillAdvertisementOptIn"
              label={translation.registration.advertisementOptInLabelAS(
                PRIVACY_STATEMENT_LINK_AS
              )}
              onChange={setAutofillAdvertisementOptIn}
              checked={autofillAdvertisementOptIn}
            />
          </div>
        )}
        <FormStatic>
          <div
            dangerouslySetInnerHTML={{
              __html: translation.registration.termsAndConditions(
                props.outro.termsAndConditionsLink
              ),
            }}
          />
          <div>
            <a
              href={props.outro.privacyPolicyLink}
              target="_blank"
              rel="noreferrer"
            >
              {translation.registration.privacy}
            </a>
          </div>
        </FormStatic>
        <FormButton
          type="submit"
          disabled={submitDisabled}
          label={translation.registration.autofillUseSavedAddress}
          onClick={handleSelectedContactSubmit}
          dataQA="registration.autofill.submit"
        />
        <FormButton
          type="button"
          label={translation.registration.autofillOtherAddress}
          onClick={() => setSmartFlowState("manual")}
          dataQA="registration.autofill.manual"
          variant="secondary"
        />
      </RegistrationStep>
    );
  };

  const renderLoginRequired = (): React.JSX.Element => (
    <div data-qa="registration.autofill.loginRequired">
      <FormStatic>
        <b>{translation.registration.autofillLoginRequired}</b>
      </FormStatic>
      <FormButton
        type="button"
        label={translation.registration.autofillLogin}
        onClick={() => IFrameService.redirectToLogin()}
        dataQA="registration.autofill.login"
      />
    </div>
  );

  const renderAlreadyRegistered = (): React.JSX.Element => {
    const summary = autofill?.profile.entrySummary;
    const summaryText = [summary?.firstName, summary?.city]
      .filter(Boolean)
      .join(", ");
    return (
      <RegistrationStep data-qa="registration.autofill.alreadyRegistered">
        <FormStatic $variant="autofillRegisteredIntro">
          <b>{translation.registration.autofillAlreadyRegistered}</b>
          <div>{translation.registration.autofillAlreadyRegisteredSubline}</div>
        </FormStatic>
        {summaryText && (
          <FormStatic
            $variant="autofillEntrySummary"
            data-qa="registration.autofill.entrySummary"
          >
            <div>{translation.registration.autofillEntrySummaryLabel}</div>
            <div>{summaryText}</div>
          </FormStatic>
        )}
        {autofill?.profile.entryContactState === "available" ? (
          <FormButton
            type="button"
            label={translation.registration.autofillUpdateAddress}
            onClick={() => {
              setContactUpdateStatus(undefined);
              setSmartFlowState("edit");
            }}
            dataQA="registration.autofill.edit"
            variant="secondary"
          />
        ) : (
          <FormStatic data-qa="registration.autofill.entryContactUnavailable">
            {translation.registration.autofillEntryContactUnavailable}
          </FormStatic>
        )}
      </RegistrationStep>
    );
  };

  const renderContactUpdateForm = (): React.JSX.Element => (
    <div
      data-qa="registration.autofill.editForm"
      style={autofillStyles.smartFlowScreen}
    >
      <ClassicRegistrationForm
        {...props}
        outro={{
          ...props.outro,
          advertisementOptIn: false,
          customFields: undefined,
          hansIdOptIn: false,
          promo: undefined,
          registrationCustomOption: false,
        }}
        initialParticipant={getEntryContactParticipant()}
        registrationStatus={contactUpdateStatus ?? RegistrationStatus.None}
        onSubmit={handleContactUpdate}
        formTitle={translation.registration.autofillEditTitle}
        submitLabel={translation.registration.autofillSaveAddress}
        successMessage={translation.registration.autofillUpdateSuccess}
        submitNotice={
          <FormStatic
            $variant="autofillEditDisclosure"
            data-qa="registration.autofill.editDisclosure"
          >
            <div>{translation.registration.autofillEditDisclosureTitle}</div>
            <div>{translation.registration.autofillEditDisclosureBody}</div>
          </FormStatic>
        }
      />
    </div>
  );

  const renderRegistrationForm = (
    saveContactConsentControl?: React.ReactNode
  ): React.JSX.Element =>
    lightLayout ? (
      <LightRegistrationForm
        {...props}
        initialParticipant={getInitialParticipant()}
        hansId={hansId}
        hansIdExists={hansIdExists}
        registrationStatus={autofillRegistrationStatus ?? registrationStatus}
        onSubmit={handleSubmit}
      />
    ) : (
      <ClassicRegistrationForm
        {...props}
        initialParticipant={getInitialParticipant()}
        hansId={hansId}
        hansIdExists={hansIdExists}
        registrationStatus={autofillRegistrationStatus ?? registrationStatus}
        onSubmit={handleSubmit}
        saveContactConsentControl={saveContactConsentControl}
      />
    );

  if (loading) {
    return renderAutofillStatus(RegistrationStatus.Pending);
  }

  if (!autofillApplies) {
    return (
      <div data-hans-id-received={!!hansId}>{renderRegistrationForm()}</div>
    );
  }

  let smartFlowContent: React.JSX.Element;
  switch (smartFlowState) {
    case "loading":
      smartFlowContent = renderAutofillStatus(RegistrationStatus.Pending);
      break;
    case "loginRequired":
      smartFlowContent = renderLoginRequired();
      break;
    case "alreadyRegistered":
      smartFlowContent = renderAlreadyRegistered();
      break;
    case "chooseAddress":
    case "identityMismatch":
      smartFlowContent = renderAutofillChooser();
      break;
    case "manual":
      smartFlowContent = renderRegistrationForm(
        autofill ? (
          <FormStatic
            $variant="autofillSaveConsentCard"
            data-qa="registration.autofill.saveContactConsent"
          >
            <div data-qa="registration.autofill.saveContactConsentTeaser">
              <div>
                {translation.registration.autofillSaveConsentTeaserTitle}
              </div>
              <div>
                {translation.registration.autofillSaveConsentTeaserBody}
              </div>
            </div>
            <FormCheckbox
              icons={icons}
              name="saveContactConsent"
              label={translation.registration.autofillSaveConsentLabel}
              onChange={setSaveContactConsent}
              checked={saveContactConsent}
            />
          </FormStatic>
        ) : undefined
      );
      break;
    case "edit":
      smartFlowContent = renderContactUpdateForm();
      break;
    default:
      smartFlowContent = renderRegistrationForm();
  }

  return <div data-hans-id-received={!!hansId}>{smartFlowContent}</div>;
};

const autofillStyles: {
  checkbox: React.CSSProperties;
  contactAction: React.CSSProperties;
  contactRow: React.CSSProperties;
  deleteConfirmation: React.CSSProperties;
  deleteConfirmationActions: React.CSSProperties;
  deleteConfirmationText: React.CSSProperties;
  legalNotice: React.CSSProperties;
  option: React.CSSProperties;
  options: React.CSSProperties;
  scrollOptions: React.CSSProperties;
  smartFlowScreen: React.CSSProperties;
} = {
  checkbox: {
    marginBottom: 18,
  },
  contactAction: {
    appearance: "none",
    backgroundColor: "transparent",
    borderStyle: "solid",
    borderWidth: 1,
    boxSizing: "border-box",
    cursor: "pointer",
    flex: "0 1 auto",
    font: "inherit",
    minHeight: 44,
    minWidth: 44,
    overflowWrap: "anywhere",
    padding: "8px 12px",
  },
  contactRow: {
    alignItems: "flex-start",
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
    maxWidth: "100%",
    minWidth: 0,
    width: "100%",
  },
  deleteConfirmation: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    maxWidth: "100%",
    minWidth: 0,
    width: "100%",
  },
  deleteConfirmationActions: {
    display: "flex",
    flex: "0 1 auto",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
    maxWidth: "100%",
    minWidth: 0,
  },
  deleteConfirmationText: {
    flex: "1 1 160px",
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  // The legal wording contains a deliberate line break; keep it rather than collapsing the text.
  legalNotice: {
    whiteSpace: "pre-line",
  },
  option: {
    alignItems: "flex-start",
    boxSizing: "border-box",
    cursor: "pointer",
    display: "flex",
    fontSize: 16,
    gap: 10,
    lineHeight: 1.35,
    flex: "1 1 180px",
    marginBottom: 0,
    minHeight: 44,
    minWidth: 0,
    overflowWrap: "anywhere",
    padding: "4px 0",
    width: "auto",
  },
  options: {
    boxSizing: "border-box",
    margin: "16px 0 18px",
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "hidden",
  },
  scrollOptions: {
    maxHeight: 264,
    overscrollBehavior: "contain",
    overflowY: "auto",
    paddingRight: 4,
  },
  smartFlowScreen: {
    maxWidth: "100%",
    minWidth: 0,
    overflowWrap: "anywhere",
    width: "100%",
  },
};
